// src/modulos/Faturamento/InvoicingIssuingPage.js

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import { db } from '../../firebaseConfig';
import {
    collection,
    addDoc,
    doc,
    updateDoc,
    serverTimestamp,
    getDocs,
    getDoc,
    query,
    where,
    writeBatch,
    deleteDoc,
    runTransaction
} from 'firebase/firestore';
import { useNotifications } from '../../contexts/NotificationContext';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Toast from '../../components/common/Toast/Toast';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import ComboBox from '../../components/common/ComboBox/ComboBox';
import {
    FiFileText,
    FiCheck,
    FiX,
    FiRefreshCw,
    FiInfo,
    FiPlus,
    FiTrash2,
    FiSearch,
    FiPrinter,
    FiPackage,
    FiDollarSign,
    FiUser,
    FiTruck,
    FiAlertCircle,
    FiTrendingUp
} from 'react-icons/fi';
import styles from '../Orders/Orders.module.css';

// ============================================
// CONSTANTS
// ============================================
const SALES_ORDERS_COLLECTION = 'sales-orders';
const CLIENTS_COLLECTION = 'clients';
const PRODUCTS_COLLECTION = 'products';
const CARRIERS_COLLECTION = 'transportadoras';
const CFOP_COLLECTION = 'parameters/data/cfop';
const NCM_COLLECTION = 'parameters/data/ncm';
const BILLING_CONDITIONS_COLLECTION = 'parameters/data/billing-conditions';
const ACCOUNT_EXECUTIVES_COLLECTION = 'account-executives';
const CURRENT_STOCK_COLLECTION = 'current-stock';
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';
const GLOBAL_SETTINGS_DOC = ['parameters', 'global-settings'];
const ITEMS_PER_PAGE = 10;

// Water materials configuration
const WATER_MATERIALS = ['MP001', 'AGUA001', 'WATER001'];

// ============================================
// FORMATTING HELPERS
// ============================================
const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCurrencyUSD = (value) => {
    if (!value && value !== 0) return '$ 0.00';
    return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
    if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
    if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
    return '-';
};

const formatDocument = (value) => {
    if (!value) return '-';
    if (value.length === 14) {
        return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    if (value.length === 11) {
        return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
};

// Check if material is water
const isWaterMaterial = (code) => {
    if (!code) return false;
    return WATER_MATERIALS.includes(code.toUpperCase());
};

// ============================================
// EXCHANGE RATE MODAL - USD → BRL Conversion at Invoicing
// ============================================
const ExchangeRateModal = ({ order, onClose, onConfirm, isLoading }) => {
    const [exchangeRate, setExchangeRate] = useState('');
    const [suggestedRate, setSuggestedRate] = useState(null);
    const [loadingRate, setLoadingRate] = useState(true);
    const [invoiceData, setInvoiceData] = useState({
        nfeNumber: '',
        series: '1',
        accessKey: '',
        cfop: '',
        issueDate: new Date().toISOString().split('T')[0],
        notes: ''
    });

    // Fetch suggested rate from global settings
    useEffect(() => {
        const fetchRate = async () => {
            try {
                const rateRef = doc(db, ...GLOBAL_SETTINGS_DOC);
                const rateSnap = await getDoc(rateRef);
                if (rateSnap.exists() && rateSnap.data().usdToBrlRate) {
                    const rate = rateSnap.data().usdToBrlRate;
                    setSuggestedRate(rate);
                    setExchangeRate(rate.toString());
                }
            } catch (err) {
                console.warn('Could not fetch suggested exchange rate:', err);
            } finally {
                setLoadingRate(false);
            }
        };
        fetchRate();
    }, []);

    if (!order) return null;

    // Order total is in USD - prefer usdValues, fall back to calculatedValues
    const totalUSD = parseFloat(order.usdValues?.total ?? order.calculatedValues?.total ?? 0);
    const itemsTotalUSD = parseFloat(order.usdValues?.itemsTotal ?? 0);
    const discountUSD = parseFloat(order.usdValues?.discountAmount ?? 0);
    const ticketDiscountUSD = parseFloat(order.usdValues?.ticketDiscountAmount ?? 0);
    const subtotalUSD = parseFloat(order.usdValues?.subtotalWithDiscounts ?? totalUSD);

    const rate = parseFloat(exchangeRate) || 0;
    const totalBRL = totalUSD * rate;
    const itemsTotalBRL = itemsTotalUSD * rate;
    const discountBRL = discountUSD * rate;
    const ticketDiscountBRL = ticketDiscountUSD * rate;
    const subtotalBRL = subtotalUSD * rate;

    const handleConfirm = () => {
        if (!exchangeRate || rate <= 0) {
            alert('Informe uma taxa de câmbio válida.');
            return;
        }
        if (!invoiceData.nfeNumber || !invoiceData.accessKey) {
            alert('Preencha o número da NF-e e a chave de acesso.');
            return;
        }

        onConfirm(order.id, {
            ...invoiceData,
            exchangeRate: rate,
            // Snapshot of USD values for audit
            usdSnapshot: {
                itemsTotal: itemsTotalUSD,
                discountAmount: discountUSD,
                ticketDiscountAmount: ticketDiscountUSD,
                subtotalWithDiscounts: subtotalUSD,
                total: totalUSD
            },
            // Converted BRL values for fiscal document
            brlValues: {
                itemsTotal: itemsTotalBRL,
                discountAmount: discountBRL,
                ticketDiscountAmount: ticketDiscountBRL,
                subtotalWithDiscounts: subtotalBRL,
                total: totalBRL
            },
            // Per-item conversion (unit and total)
            itemsConverted: (order.items || []).map(item => {
                const unitUSD = parseFloat(item.unitPrice) || 0;
                const totalItemUSD = parseFloat(item.total) || (unitUSD * (parseFloat(item.quantity) || 0));
                return {
                    ...item,
                    unitPriceUSD: unitUSD,
                    totalUSD: totalItemUSD,
                    unitPriceBRL: unitUSD * rate,
                    totalBRL: totalItemUSD * rate
                };
            })
        });
    };

    return ReactDOM.createPortal(
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div
                className={`${styles.formContainer} ${styles.formContainerMedium}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <div>
                        <h2>
                            <FiFileText size={24} style={{ marginRight: '10px' }} />
                            Lançamento de Nota Fiscal
                        </h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> — {order.clientName}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.modalCloseButton}
                        disabled={isLoading}
                        aria-label="Fechar"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    {/* SECTION: Exchange Rate */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>
                                <FiDollarSign size={18} />
                            </div>
                            <h3 className={styles.sectionTitle}>Taxa de Câmbio (USD → BRL)</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <div
                                className={styles.infoBox}
                                style={{
                                    backgroundColor: 'var(--info-light)',
                                    borderLeftColor: 'var(--info-color)',
                                    marginBottom: '16px'
                                }}
                            >
                                <FiInfo size={20} />
                                <span>
                                    O pedido está armazenado em <strong>USD</strong>. Informe a taxa
                                    de câmbio do dia da emissão para que os valores sejam
                                    convertidos para <strong>BRL</strong> na nota fiscal.
                                </span>
                            </div>

                            <label className={styles.inputLabel}>
                                Taxa de Câmbio (R$ por USD) <span className={styles.required}>*</span>
                            </label>
                            <input
                                type="number"
                                step="0.0001"
                                min="0"
                                className={styles.input}
                                value={exchangeRate}
                                onChange={(e) => setExchangeRate(e.target.value)}
                                placeholder="Ex: 5.2500"
                                disabled={isLoading || loadingRate}
                            />
                            {suggestedRate && (
                                <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                                    Taxa sugerida (parâmetros): R$ {suggestedRate.toFixed(4)}
                                </small>
                            )}
                        </div>
                    </div>

                    {/* SECTION: Fiscal Data */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>
                                <FiFileText size={18} />
                            </div>
                            <h3 className={styles.sectionTitle}>Dados Fiscais</h3>
                        </div>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '15px',
                                padding: '20px'
                            }}
                        >
                            <div>
                                <label className={styles.inputLabel}>
                                    Número da NF-e <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={invoiceData.nfeNumber}
                                    onChange={(e) =>
                                        setInvoiceData({ ...invoiceData, nfeNumber: e.target.value })
                                    }
                                    placeholder="Ex: 123456"
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label className={styles.inputLabel}>Série</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={invoiceData.series}
                                    onChange={(e) =>
                                        setInvoiceData({ ...invoiceData, series: e.target.value })
                                    }
                                    disabled={isLoading}
                                />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label className={styles.inputLabel}>
                                    Chave de Acesso (44 dígitos) <span className={styles.required}>*</span>
                                </label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={invoiceData.accessKey}
                                    onChange={(e) =>
                                        setInvoiceData({ ...invoiceData, accessKey: e.target.value })
                                    }
                                    placeholder="Ex: 3523..."
                                    maxLength={44}
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label className={styles.inputLabel}>CFOP</label>
                                <input
                                    type="text"
                                    className={styles.input}
                                    value={invoiceData.cfop}
                                    onChange={(e) =>
                                        setInvoiceData({ ...invoiceData, cfop: e.target.value })
                                    }
                                    placeholder="Ex: 5101"
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label className={styles.inputLabel}>Data de Emissão</label>
                                <input
                                    type="date"
                                    className={styles.input}
                                    value={invoiceData.issueDate}
                                    onChange={(e) =>
                                        setInvoiceData({ ...invoiceData, issueDate: e.target.value })
                                    }
                                    disabled={isLoading}
                                />
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label className={styles.inputLabel}>Observações Fiscais</label>
                                <textarea
                                    className={styles.textarea}
                                    rows={2}
                                    value={invoiceData.notes}
                                    onChange={(e) =>
                                        setInvoiceData({ ...invoiceData, notes: e.target.value })
                                    }
                                    placeholder="Informações adicionais da nota..."
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* SECTION: Conversion Summary */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}>
                                <FiTrendingUp size={18} />
                            </div>
                            <h3 className={styles.sectionTitle}>Resumo da Conversão</h3>
                        </div>
                        <div style={{ padding: '16px 20px' }}>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    fontSize: '0.9rem'
                                }}
                            >
                                <thead>
                                    <tr style={{ borderBottom: '1px solid var(--orders-border-color)' }}>
                                        <th style={{ textAlign: 'left', padding: '6px 4px' }}>Descrição</th>
                                        <th style={{ textAlign: 'right', padding: '6px 4px' }}>USD</th>
                                        <th style={{ textAlign: 'right', padding: '6px 4px' }}>BRL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ padding: '6px 4px' }}>Total de Produtos</td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            {formatCurrencyUSD(itemsTotalUSD)}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            {formatCurrency(itemsTotalBRL)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '6px 4px' }}>Desconto do Cliente</td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            - {formatCurrencyUSD(discountUSD)}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            - {formatCurrency(discountBRL)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '6px 4px' }}>Desconto de Ticket</td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            - {formatCurrencyUSD(ticketDiscountUSD)}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            - {formatCurrency(ticketDiscountBRL)}
                                        </td>
                                    </tr>
                                    <tr style={{ borderTop: '1px solid var(--orders-border-color)' }}>
                                        <td style={{ padding: '6px 4px' }}>Subtotal com Descontos</td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            {formatCurrencyUSD(subtotalUSD)}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '6px 4px' }}>
                                            {formatCurrency(subtotalBRL)}
                                        </td>
                                    </tr>
                                    <tr
                                        style={{
                                            borderTop: '2px solid var(--orders-primary-color)',
                                            fontWeight: '700',
                                            color: 'var(--orders-primary-color)'
                                        }}
                                    >
                                        <td style={{ padding: '8px 4px' }}>Total da Nota</td>
                                        <td style={{ textAlign: 'right', padding: '8px 4px' }}>
                                            {formatCurrencyUSD(totalUSD)}
                                        </td>
                                        <td style={{ textAlign: 'right', padding: '8px 4px' }}>
                                            {formatCurrency(totalBRL)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Order Summary */}
                    <div className={styles.orderSummary}>
                        <div className={styles.orderSummaryTitle}>Resumo do Pedido</div>
                        <div className={styles.orderSummaryGrid}>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Caixas Embaladas:</span>
                                <span className={styles.orderSummaryValue}>
                                    {order.totalBoxes || 0}
                                </span>
                            </div>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Peso Bruto:</span>
                                <span className={styles.orderSummaryValue}>
                                    {parseFloat(order.grossWeight || 0).toFixed(3)} kg
                                </span>
                            </div>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Itens:</span>
                                <span className={styles.orderSummaryValue}>
                                    {order.items?.length || 0}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button
                        type="button"
                        onClick={onClose}
                        className={styles.secondaryButton}
                        disabled={isLoading}
                    >
                        <FiX size={18} /> Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        className={styles.confirmButton}
                        disabled={
                            isLoading ||
                            loadingRate ||
                            !exchangeRate ||
                            rate <= 0 ||
                            !invoiceData.nfeNumber ||
                            !invoiceData.accessKey
                        }
                    >
                        {isLoading ? (
                            <>
                                <FiRefreshCw size={18} className={styles.buttonSpinner} /> Faturando...
                            </>
                        ) : (
                            <>
                                <FiCheck size={18} /> Confirmar Lançamento
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

// ============================================
// MANUAL NFe CREATION MODAL
// ============================================
const ManualInvoiceModal = ({ onClose, onConfirm, isLoading }) => {
    const { currentUser } = useAuth();
    const { data: cfopList, loading: cfopLoading } = useFirestore(CFOP_COLLECTION);
    const { data: clients, loading: clientsLoading } = useFirestore(CLIENTS_COLLECTION, 'razaoSocial');
    const { data: productsData, loading: productsLoading } = useFirestore(PRODUCTS_COLLECTION, 'commercialName');
    const { data: carriersData, loading: carriersLoading } = useFirestore(CARRIERS_COLLECTION, 'razaoSocial');

    const { data: billingConditions, loading: billingConditionsLoading } = useFirestore(BILLING_CONDITIONS_COLLECTION, 'name');
    const { data: accountExecutives, loading: accountExecutivesLoading } = useFirestore(ACCOUNT_EXECUTIVES_COLLECTION, 'nome');

    const [selectedCarrier, setSelectedCarrier] = useState(null);

    // Stock Warning Modal States
    const [showStockWarning, setShowStockWarning] = useState(false);
    const [stockWarningProduct, setStockWarningProduct] = useState(null);
    const [pendingSubmit, setPendingSubmit] = useState(null);

    // Section 1: Header Data
    const [header, setHeader] = useState({
        inventory: 'B2E - Disponível',
        operationNature: '',
        isComplementary: false,
        billTo: 'Cliente',
        entryExitDate: new Date().toISOString().split('T')[0],
        diCode: '',
        osCode: '',
        transferCode: '',
        clientId: '',
        clientCode: '',
        clientName: '',
        clientDocument: '',
        billingCondition: '',
        accountExecutive: '',
        address: '',
        phone: '',
        email: '',
        generateBoleto: false,
        boletoOption: '',
        duplicates: ''
    });

    // Section 2: Products - Now using a separate combobox for adding products
    const [products, setProducts] = useState([]);

    // Section 3: Totals & Transport
    const [totalsAndTransport, setTotalsAndTransport] = useState({
        calcBaseIcms: 0,
        stBaseIcms: 0,
        freightValue: 0,
        otherExpenses: 0,
        totalProducts: 0,
        icmsValue: 0,
        stValue: 0,
        ipiTotalValue: 0,
        insuranceValue: 0,
        totalDiscounts: 0,
        totalInvoice: 0,
        carrierId: '',
        carrierCode: '',
        carrierName: '',
        carrierDocument: '',
        freightPayer: 'emitente',
        vehiclePlate: '',
        vehicleUf: '',
        quantity: '',
        species: '',
        brand: '',
        number: '',
        grossWeight: 0,
        netWeight: 0,
        legalMessages: '',
        complementaryInfo: ''
    });

    // Options mapping
    const clientOptions = useMemo(() => {
        if (!clients) return [];
        return clients
            .filter(client => client.situacao !== 'Inativo')
            .sort((a, b) => (a.razaoSocial || '').localeCompare(b.razaoSocial || ''))
            .map(client => ({
                value: client.id,
                label: `${client.codigo || ''} - ${client.razaoSocial || ''}`,
                ...client
            }));
    }, [clients]);

    const productOptions = useMemo(() => {
        if (!productsData) return [];
        return productsData
            .filter(p => p.situation !== 'Inativo')
            .sort((a, b) => (a.commercialName || '').localeCompare(b.commercialName || ''))
            .map(p => ({
                value: p.id,
                label: `${p.code || ''} - ${p.commercialName || ''}`,
                ...p
            }));
    }, [productsData]);

    // Filter out already selected products
    const availableProductOptions = useMemo(() => {
        const selectedIds = new Set(products.map(p => p.productId).filter(id => id));
        return productOptions.filter(option => !selectedIds.has(option.value));
    }, [productOptions, products]);

    const carrierOptions = useMemo(() => {
        if (!carriersData) return [];
        return carriersData
            .filter(c => c.situacao !== 'Inativo')
            .sort((a, b) => (a.razaoSocial || '').localeCompare(b.razaoSocial || ''))
            .map(c => ({
                value: c.id,
                label: `${c.codigo || ''} - ${c.razaoSocial || ''}`,
                ...c
            }));
    }, [carriersData]);

    const cfopOptions = useMemo(() => {
        if (!cfopList) return [];
        return cfopList
            .filter(cfop => cfop.codigoOperacao)
            .sort((a, b) => (a.codigoOperacao || '').localeCompare(b.codigoOperacao || '', undefined, { numeric: true }));
    }, [cfopList]);

    const billingConditionOptions = useMemo(() => {
        if (!billingConditions) return [];
        return billingConditions
            .filter(condition => condition.name && condition.days)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(condition => ({
                value: condition.id,
                label: `${condition.name} (${condition.days.replace(/\//g, ' / ')} dias)`,
                name: condition.name,
                days: condition.days
            }));
    }, [billingConditions]);

    const accountExecutiveOptions = useMemo(() => {
        if (!accountExecutives) return [];
        return accountExecutives
            .filter(exec => exec.situacao === 'ativo' && exec.nome)
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map(exec => ({
                value: exec.id,
                label: `${exec.nome}${exec.emailUsuario ? ` (${exec.emailUsuario})` : ''}`,
                nome: exec.nome,
                email: exec.emailUsuario,
                comissaoPadrao: exec.comissaoPadrao || 0
            }));
    }, [accountExecutives]);

    // Handle client selection
    const handleClientSelect = (selectedId) => {
        const selectedClient = clients?.find(c => c.id === selectedId);

        if (!selectedClient) {
            setHeader(prev => ({
                ...prev,
                clientId: '',
                clientCode: '',
                clientName: '',
                clientDocument: '',
                address: '',
                phone: '',
                email: '',
                billingCondition: '',
                accountExecutive: ''
            }));
            return;
        }

        const address = selectedClient.addresses?.[0] || {};
        const contact = address.contacts?.[0] || {};

        setHeader(prev => ({
            ...prev,
            clientId: selectedClient.id,
            clientCode: selectedClient.codigo || '',
            clientName: selectedClient.razaoSocial || '',
            clientDocument: selectedClient.cnpjCpf || '',
            address: `${address.logradouro || ''}, ${address.numero || ''} - ${address.bairro || ''}, ${address.cidade || ''}/${address.uf || ''}`,
            phone: contact.telefone || '',
            email: contact.email || '',
            billingCondition: selectedClient.condicaoFaturamentoId || '',
            accountExecutive: selectedClient.accountExecutiveId || ''
        }));
    };

    // Handle carrier selection
    const handleCarrierSelect = (carrierId) => {
        const carrier = carriersData?.find(c => c.id === carrierId);

        if (!carrier) {
            setSelectedCarrier(null);
            setTotalsAndTransport(prev => ({
                ...prev,
                carrierId: '',
                carrierCode: '',
                carrierName: '',
                carrierDocument: '',
                vehiclePlate: '',
                vehicleUf: ''
            }));
            return;
        }

        setSelectedCarrier(carrier);
        setTotalsAndTransport(prev => ({
            ...prev,
            carrierId: carrier.id,
            carrierCode: carrier.codigo || '',
            carrierName: carrier.razaoSocial || '',
            carrierDocument: carrier.cnpjCpf || '',
            vehiclePlate: '',
            vehicleUf: ''
        }));
    };

    // Handle adding a product from the combobox - fetches actual stock
    const handleAddProduct = async (productId) => {
        if (!productId) return;

        const product = productsData?.find(p => p.id === productId);
        if (!product) return;

        // Fetch actual stock from current-stock collection
        let actualStock = 0;
        try {
            const stockQuery = query(
                collection(db, CURRENT_STOCK_COLLECTION),
                where("rawMaterialId", "==", productId),
                where("quantity", ">", 0.001)
            );
            const stockSnapshot = await getDocs(stockQuery);
            stockSnapshot.forEach(docSnap => {
                actualStock += docSnap.data().quantity || 0;
            });
        } catch (err) {
            console.warn('Could not fetch stock for product', productId, err);
        }

        const fiscal = product.fiscal || {};
        const suggestedPrice = (product.totalFormulationCost || 0) * (product.markup || 1);

        const newProduct = {
            rowId: crypto.randomUUID(),
            productId: product.id,
            itemCode: product.code || '',
            description: product.commercialName || '',
            cfop: fiscal.cfopSaidaEstadual || '',
            ncm: fiscal.ncmId || '',
            taxClass: fiscal.ncmId || '',
            situacaoTributaria: '',
            unit: product.technical?.unit || 'UN',
            quantity: 1,
            unitValue: suggestedPrice,
            totalValue: suggestedPrice,
            icms: 0,
            ipi: 0,
            ipiValue: 0,
            discountPercent: 0,
            discountValue: 0,
            lot: '',
            stockAvailable: actualStock,
            expirationDate: null,
            isWater: isWaterMaterial(product.code)
        };

        setProducts(prev => [...prev, newProduct]);
    };

    // Handle product change with situacaoTributaria support
    const handleProductChange = (index, field, value) => {
        setProducts(prev => {
            const updated = [...prev];
            const item = { ...updated[index] };

            if (field === 'quantity' || field === 'unitValue' || field === 'discountValue' ||
                field === 'icms' || field === 'ipi' || field === 'ipiValue' || field === 'discountPercent') {
                const numValue = parseFloat(value) || 0;
                item[field] = numValue;
            } else if (field === 'cfop') {
                const selectedCfop = cfopList?.find(cfop => cfop.codigoOperacao === value);
                item.cfop = value;

                if (selectedCfop && selectedCfop.classificacaoTributariaRt) {
                    item.ncm = selectedCfop.classificacaoTributariaRt;
                } else {
                    item.ncm = '';
                }

                if (selectedCfop && selectedCfop.situacaoTributariaIcmsPadrao) {
                    item.situacaoTributaria = selectedCfop.situacaoTributariaIcmsPadrao;
                }
            } else {
                item[field] = value;
            }

            if (field === 'quantity' || field === 'unitValue' || field === 'discountValue') {
                const qty = parseFloat(item.quantity) || 0;
                const unitV = parseFloat(item.unitValue) || 0;
                const desc = parseFloat(item.discountValue) || 0;
                item.totalValue = Math.max(0, (qty * unitV) - desc);
            }

            updated[index] = item;
            return updated;
        });
    };

    const handleRemoveProduct = (index) => {
        setProducts(prev => prev.filter((_, i) => i !== index));
    };

    const handleCalculateTotals = () => {
        const productTotal = products.reduce((acc, p) => acc + (parseFloat(p.totalValue) || 0), 0);
        const totalDesc = products.reduce((acc, p) => acc + (parseFloat(p.discountValue) || 0), 0);
        const totalIpi = products.reduce((acc, p) => acc + (parseFloat(p.ipiValue) || 0), 0);
        const totalIcms = products.reduce((acc, p) => acc + (parseFloat(p.icms) || 0), 0);

        const freight = parseFloat(totalsAndTransport.freightValue) || 0;
        const insurance = parseFloat(totalsAndTransport.insuranceValue) || 0;
        const expenses = parseFloat(totalsAndTransport.otherExpenses) || 0;

        const grandTotal = productTotal + totalIpi + freight + insurance + expenses;

        setTotalsAndTransport(prev => ({
            ...prev,
            totalProducts: productTotal,
            totalDiscounts: totalDesc,
            ipiTotalValue: totalIpi,
            icmsValue: totalIcms,
            calcBaseIcms: productTotal,
            totalInvoice: grandTotal
        }));
    };

    useEffect(() => {
        handleCalculateTotals();
    }, [products]);

    const doSubmit = () => {
        onConfirm({
            header,
            products: products.map(p => ({
                ...p,
                quantity: parseFloat(p.quantity) || 0,
                unitValue: parseFloat(p.unitValue) || 0,
                totalValue: parseFloat(p.totalValue) || 0,
                icms: parseFloat(p.icms) || 0,
                ipi: parseFloat(p.ipi) || 0,
                ipiValue: parseFloat(p.ipiValue) || 0,
                discountPercent: parseFloat(p.discountPercent) || 0,
                discountValue: parseFloat(p.discountValue) || 0,
            })),
            totalsAndTransport: {
                ...totalsAndTransport,
                freightValue: parseFloat(totalsAndTransport.freightValue) || 0,
                otherExpenses: parseFloat(totalsAndTransport.otherExpenses) || 0,
                totalProducts: parseFloat(totalsAndTransport.totalProducts) || 0,
                icmsValue: parseFloat(totalsAndTransport.icmsValue) || 0,
                stValue: parseFloat(totalsAndTransport.stValue) || 0,
                ipiTotalValue: parseFloat(totalsAndTransport.ipiTotalValue) || 0,
                insuranceValue: parseFloat(totalsAndTransport.insuranceValue) || 0,
                totalDiscounts: parseFloat(totalsAndTransport.totalDiscounts) || 0,
                totalInvoice: parseFloat(totalsAndTransport.totalInvoice) || 0,
                grossWeight: parseFloat(totalsAndTransport.grossWeight) || 0,
                netWeight: parseFloat(totalsAndTransport.netWeight) || 0,
            },
            issuedBy: currentUser?.nome || currentUser?.email || 'Usuário',
            createdAt: new Date().toISOString(),
            clientData: clients?.find(c => c.id === header.clientId),
            carrierData: selectedCarrier,
        });
    };

    const handleSubmit = () => {
        if (!header.operationNature) {
            alert('Por favor, selecione a Natureza da Operação.');
            return;
        }
        if (!header.clientName) {
            alert('Por favor, selecione um cliente.');
            return;
        }
        if (products.length === 0) {
            alert('Por favor, adicione pelo menos um produto.');
            return;
        }

        const productWithIssue = products.find(p =>
            p.productId && p.quantity > p.stockAvailable && !p.isWater
        );

        if (productWithIssue) {
            setStockWarningProduct({
                description: productWithIssue.description || productWithIssue.itemCode || 'Produto',
                quantity: productWithIssue.quantity,
                stockAvailable: productWithIssue.stockAvailable
            });
            setPendingSubmit(() => doSubmit);
            setShowStockWarning(true);
            return;
        }

        doSubmit();
    };

    return (
        <>
            {showStockWarning && ReactDOM.createPortal(
                <div className={styles.modalBackdrop} onClick={() => setShowStockWarning(false)}>
                    <div className={`${styles.formContainer} ${styles.formContainerSmall}`} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <div>
                                <h2 style={{ color: '#f57c00' }}>
                                    <FiAlertCircle size={24} style={{ marginRight: '10px', color: '#f57c00' }} />
                                    Atenção: Estoque Insuficiente
                                </h2>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowStockWarning(false)}
                                className={styles.modalCloseButton}
                                disabled={isLoading}
                            >
                                <FiX size={24} />
                            </button>
                        </div>
                        <div className={styles.formContent}>
                            <div className={styles.infoBox} style={{ backgroundColor: '#fff3e0', borderLeftColor: '#f57c00' }}>
                                <FiAlertCircle size={20} color="#f57c00" />
                                <span>
                                    O produto <strong>"{stockWarningProduct?.description}"</strong> tem
                                    quantidade solicitada <strong>{stockWarningProduct?.quantity}</strong>
                                    maior que o estoque disponível <strong>{stockWarningProduct?.stockAvailable}</strong>.
                                </span>
                            </div>
                            <p style={{ color: '#666', marginTop: '16px', textAlign: 'center' }}>
                                Deseja continuar com o lançamento mesmo assim?
                            </p>
                        </div>
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                onClick={() => setShowStockWarning(false)}
                                className={styles.secondaryButton}
                                disabled={isLoading}
                            >
                                <FiX size={18} /> Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowStockWarning(false);
                                    if (pendingSubmit) {
                                        pendingSubmit();
                                        setPendingSubmit(null);
                                    }
                                }}
                                className={styles.confirmButton}
                                style={{ backgroundColor: '#f57c00', borderColor: '#f57c00' }}
                                disabled={isLoading}
                            >
                                <FiCheck size={18} /> Continuar Mesmo Assim
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <div className={styles.modalBackdrop} onClick={onClose}>
                <div className={`${styles.formContainer} ${styles.formContainerXLarge}`} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <div>
                            <h2><FiFileText size={24} style={{ marginRight: '10px' }} /> Lançamento Manual de Notas Fiscais</h2>
                            <p className={styles.modalSubtitle}>
                                Preencha os dados da NF-e manualmente
                            </p>
                        </div>
                        <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={isLoading} aria-label="Fechar">
                            <FiX size={24} />
                        </button>
                    </div>

                    <div className={styles.formContent}>
                        {/* SECTION 1: HEADER FORM */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiFileText size={18} /></div>
                                <h3 className={styles.sectionTitle}>Dados Gerais da NF-e</h3>
                            </div>
                            <div className={styles.formGrid}>
                                <div className={styles.inputGroup}>
                                    <label>Inventário</label>
                                    <select
                                        className={styles.select}
                                        value={header.inventory}
                                        onChange={(e) => setHeader({ ...header, inventory: e.target.value })}
                                        disabled={isLoading}
                                    >
                                        <option value="B2E - Disponível">B2E - Disponível</option>
                                        <option value="Almoxarifado Central">Almoxarifado Central</option>
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Natureza da Operação <span className={styles.required}>*</span></label>
                                    <select
                                        className={styles.select}
                                        value={header.operationNature}
                                        onChange={(e) => setHeader({ ...header, operationNature: e.target.value })}
                                        disabled={isLoading || cfopLoading}
                                    >
                                        <option value="">- Selecione -</option>
                                        {cfopOptions.map(cfop => (
                                            <option key={cfop.id} value={cfop.codigoOperacao}>
                                                {cfop.codigoOperacao} - {cfop.descricao}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}></div>

                                <div className={styles.inputGroup} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={header.isComplementary}
                                            onChange={(e) => setHeader({ ...header, isComplementary: e.target.checked })}
                                            disabled={isLoading}
                                        />
                                        NF complementar
                                    </label>
                                </div>

                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <label style={{ fontWeight: '600', fontSize: '0.95rem', color: 'var(--orders-text-primary)' }}>Faturar para:</label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="billTo"
                                            value="Cliente"
                                            checked={header.billTo === 'Cliente'}
                                            onChange={(e) => setHeader({ ...header, billTo: e.target.value })}
                                            disabled={isLoading}
                                        />
                                        Cliente
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="billTo"
                                            value="Fornecedor"
                                            checked={header.billTo === 'Fornecedor'}
                                            onChange={(e) => setHeader({ ...header, billTo: e.target.value })}
                                            disabled={isLoading}
                                        />
                                        Fornecedor
                                    </label>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Data Saída / Entrada</label>
                                    <input
                                        type="date"
                                        className={styles.input}
                                        value={header.entryExitDate}
                                        onChange={(e) => setHeader({ ...header, entryExitDate: e.target.value })}
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Código da DI</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={header.diCode}
                                        onChange={(e) => setHeader({ ...header, diCode: e.target.value })}
                                        placeholder="Código da DI"
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Código da O.S.</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={header.osCode}
                                        onChange={(e) => setHeader({ ...header, osCode: e.target.value })}
                                        placeholder="Código da O.S."
                                        disabled={isLoading}
                                    />
                                </div>

                                {/* CLIENT COMBOBOX */}
                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <ComboBox
                                        label="Cliente"
                                        required
                                        options={clientOptions}
                                        value={header.clientId}
                                        onChange={handleClientSelect}
                                        placeholder="Digite para buscar ou selecione um cliente..."
                                        disabled={isLoading}
                                        loading={clientsLoading}
                                        noResultsMessage="Nenhum cliente encontrado"
                                    />
                                    {header.clientName && (
                                        <div style={{ marginTop: '4px', fontSize: '0.85rem', color: '#2e7d32', fontWeight: 'bold' }}>
                                            Cliente Selecionado: {header.clientName}
                                        </div>
                                    )}
                                    {header.clientDocument && (
                                        <small style={{ color: '#666', display: 'block', marginTop: '2px' }}>
                                            CNPJ/CPF: {formatDocument(header.clientDocument)}
                                        </small>
                                    )}
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Condição de Faturamento</label>
                                    <select
                                        className={styles.select}
                                        value={header.billingCondition}
                                        onChange={(e) => setHeader({ ...header, billingCondition: e.target.value })}
                                        disabled={isLoading || billingConditionsLoading}
                                    >
                                        <option value="">- Selecione -</option>
                                        {billingConditionOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Executivo de Contas</label>
                                    <select
                                        className={styles.select}
                                        value={header.accountExecutive}
                                        onChange={(e) => setHeader({ ...header, accountExecutive: e.target.value })}
                                        disabled={isLoading || accountExecutivesLoading}
                                    >
                                        <option value="">- Selecione -</option>
                                        {accountExecutiveOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                                    <label>Endereço</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={header.address}
                                        onChange={(e) => setHeader({ ...header, address: e.target.value })}
                                        placeholder="Endereço completo"
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Telefone</label>
                                    <input
                                        type="text"
                                        className={styles.input}
                                        value={header.phone}
                                        onChange={(e) => setHeader({ ...header, phone: e.target.value })}
                                        placeholder="Telefone"
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        className={styles.input}
                                        value={header.email}
                                        onChange={(e) => setHeader({ ...header, email: e.target.value })}
                                        placeholder="Email"
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={header.generateBoleto}
                                            onChange={(e) => setHeader({ ...header, generateBoleto: e.target.checked })}
                                            disabled={isLoading}
                                        />
                                        Gera Boleto
                                    </label>
                                    <select
                                        className={styles.select}
                                        value={header.boletoOption}
                                        onChange={(e) => setHeader({ ...header, boletoOption: e.target.value })}
                                        disabled={isLoading || !header.generateBoleto}
                                    >
                                        <option value="">- Selecione -</option>
                                        <option value="Boleto Bancário">Boleto Bancário</option>
                                        <option value="Boleto Registrado">Boleto Registrado</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* SECTION 2: PRODUCTS - ComboBox outside table */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiPackage size={18} /></div>
                                <h3 className={styles.sectionTitle}>Produtos</h3>
                                <span className={styles.sectionBadge}>{products.length} itens</span>
                            </div>

                            {/* Product ComboBox - Outside the table */}
                            <div style={{ padding: '12px 20px', backgroundColor: 'var(--orders-bg-secondary)', borderBottom: '1px solid var(--orders-border-color)' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px' }}>
                                    <div style={{ flex: 1 }}>
                                        <ComboBox
                                            label="Adicionar Produto"
                                            options={availableProductOptions}
                                            value=""
                                            onChange={handleAddProduct}
                                            placeholder="Digite para buscar ou selecione um produto..."
                                            disabled={isLoading || productsLoading}
                                            loading={productsLoading}
                                            noResultsMessage={products.length > 0 ? "Todos os produtos já foram adicionados" : "Nenhum produto encontrado"}
                                        />
                                    </div>
                                    <div style={{ paddingBottom: '2px' }}>
                                        <span style={{ fontSize: '0.85rem', color: 'var(--orders-text-secondary)' }}>
                                            {products.length > 0 ? `${products.length} produto(s) adicionado(s)` : 'Nenhum produto adicionado'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Products Table */}
                            <div style={{ padding: '0 20px 16px 20px', overflowX: 'auto' }}>
                                {products.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '40px 20px',
                                        color: 'var(--orders-text-secondary)',
                                        border: '2px dashed var(--orders-border-color)',
                                        borderRadius: '8px',
                                        marginTop: '16px'
                                    }}>
                                        <FiPackage size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                                        <p>Nenhum produto adicionado.</p>
                                        <p style={{ fontSize: '0.85rem' }}>Selecione um produto no campo acima para adicionar.</p>
                                    </div>
                                ) : (
                                    <div className={styles.tableContainer} style={{ boxShadow: 'none', border: '1px solid var(--orders-border-color)', marginTop: '16px' }}>
                                        <table className={styles.table} style={{ fontSize: '0.85rem', minWidth: '1550px' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ minWidth: '80px' }}>Cód Item</th>
                                                    <th style={{ minWidth: '220px' }}>Produto</th>
                                                    <th style={{ minWidth: '70px' }}>CFOP</th>
                                                    <th style={{ minWidth: '100px' }}>Classif. Fiscal</th>
                                                    <th style={{ minWidth: '110px' }}>Situação Tributária</th>
                                                    <th style={{ minWidth: '50px' }}>Unid.</th>
                                                    <th style={{ minWidth: '60px' }}>Quant.</th>
                                                    <th style={{ minWidth: '80px' }}>Vlr. Unit.</th>
                                                    <th style={{ minWidth: '80px' }}>Valor Total</th>
                                                    <th style={{ minWidth: '50px' }}>ICMS</th>
                                                    <th style={{ minWidth: '50px' }}>IPI</th>
                                                    <th style={{ minWidth: '70px' }}>Vl. IPI</th>
                                                    <th style={{ minWidth: '50px' }}>% Desc.</th>
                                                    <th style={{ minWidth: '70px' }}>Vlr. Desc.</th>
                                                    <th style={{ minWidth: '70px' }}>Lote</th>
                                                    <th style={{ minWidth: '40px' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {products.map((prod, idx) => (
                                                    <tr key={prod.rowId || idx}>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className={styles.input}
                                                                style={{ fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.itemCode}
                                                                readOnly
                                                            />
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong style={{ fontSize: '0.85rem' }}>{prod.description}</strong>
                                                                {prod.stockAvailable > 0 && !prod.isWater && (
                                                                    <small style={{ color: '#2e7d32', fontSize: '0.7rem', display: 'block', marginTop: '2px' }}>
                                                                        Estoque disponível: {prod.stockAvailable}
                                                                    </small>
                                                                )}
                                                                {prod.isWater && (
                                                                    <small style={{ color: '#1565c0', fontSize: '0.7rem', display: 'block', marginTop: '2px' }}>
                                                                        ♾️ Recurso infinito (Água)
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <select
                                                                className={styles.select}
                                                                style={{ fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px', width: '100%' }}
                                                                value={prod.cfop}
                                                                onChange={(e) => handleProductChange(idx, 'cfop', e.target.value)}
                                                                disabled={isLoading || cfopLoading}
                                                            >
                                                                <option value="">Sel.</option>
                                                                {cfopOptions.map(cfop => (
                                                                    <option key={cfop.id} value={cfop.codigoOperacao}>
                                                                        {cfop.codigoOperacao}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.ncm}
                                                                onChange={(e) => handleProductChange(idx, 'ncm', e.target.value)}
                                                                disabled={isLoading}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.situacaoTributaria || ''}
                                                                onChange={(e) => handleProductChange(idx, 'situacaoTributaria', e.target.value)}
                                                                disabled={isLoading}
                                                                placeholder="Ex: 00, 10, 20..."
                                                            />
                                                        </td>
                                                        <td>
                                                            <select
                                                                className={styles.select}
                                                                style={{ fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px', width: '100%' }}
                                                                value={prod.unit}
                                                                onChange={(e) => handleProductChange(idx, 'unit', e.target.value)}
                                                                disabled={isLoading}
                                                            >
                                                                <option value="UN">UN</option>
                                                                <option value="KG">KG</option>
                                                                <option value="CX">CX</option>
                                                                <option value="LT">LT</option>
                                                                <option value="M">M</option>
                                                                <option value="M²">M²</option>
                                                                <option value="M³">M³</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.quantity}
                                                                onChange={(e) => handleProductChange(idx, 'quantity', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                                min="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.unitValue}
                                                                onChange={(e) => handleProductChange(idx, 'unitValue', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <strong style={{ color: 'var(--orders-primary-color)' }}>
                                                                {formatCurrency(prod.totalValue)}
                                                            </strong>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.icms}
                                                                onChange={(e) => handleProductChange(idx, 'icms', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.ipi}
                                                                onChange={(e) => handleProductChange(idx, 'ipi', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.ipiValue}
                                                                onChange={(e) => handleProductChange(idx, 'ipiValue', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.discountPercent}
                                                                onChange={(e) => handleProductChange(idx, 'discountPercent', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.discountValue}
                                                                onChange={(e) => handleProductChange(idx, 'discountValue', e.target.value)}
                                                                disabled={isLoading}
                                                                step="0.01"
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="text"
                                                                className={styles.input}
                                                                style={{ width: '100%', fontSize: '0.8rem', padding: '4px 6px', minHeight: '30px' }}
                                                                value={prod.lot}
                                                                onChange={(e) => handleProductChange(idx, 'lot', e.target.value)}
                                                                disabled={isLoading}
                                                                placeholder="Lote"
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveProduct(idx)}
                                                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                                                disabled={isLoading}
                                                                title="Remover item"
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* SECTION 3: TOTALS & TRANSPORT */}
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionIcon}><FiDollarSign size={18} /></div>
                                <h3 className={styles.sectionTitle}>Totais e Transporte</h3>
                            </div>
                            <div style={{ padding: '16px 20px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px', alignItems: 'center' }}>
                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Base Cálculo ICMS:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.calcBaseIcms)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Base ICMS Subst.:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.stBaseIcms)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Valor Frete:</label>
                                        <input
                                            type="number"
                                            className={styles.input}
                                            value={totalsAndTransport.freightValue}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, freightValue: e.target.value })}
                                            disabled={isLoading}
                                            step="0.01"
                                        />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Outras Despesas:</label>
                                        <input
                                            type="number"
                                            className={styles.input}
                                            value={totalsAndTransport.otherExpenses}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, otherExpenses: e.target.value })}
                                            disabled={isLoading}
                                            step="0.01"
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px', alignItems: 'center' }}>
                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Valor Total Produtos:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.totalProducts)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Valor ICMS:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.icmsValue)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Valor ICMS Subst.:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.stValue)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Valor Total IPI:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.ipiTotalValue)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Valor Seguro:</label>
                                        <input
                                            type="number"
                                            className={styles.input}
                                            value={totalsAndTransport.insuranceValue}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, insuranceValue: e.target.value })}
                                            disabled={isLoading}
                                            step="0.01"
                                        />

                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Total de Descontos:</label>
                                        <input type="text" className={styles.input} readOnly value={formatCurrency(totalsAndTransport.totalDiscounts)} style={{ backgroundColor: 'var(--orders-bg-tertiary)' }} />

                                        <label style={{ fontWeight: '700', fontSize: '1rem', color: 'var(--orders-primary-color)' }}>Total da Nota:</label>
                                        <input
                                            type="text"
                                            className={styles.input}
                                            readOnly
                                            style={{ fontWeight: 'bold', backgroundColor: 'var(--orders-primary-color)', color: 'white' }}
                                            value={formatCurrency(totalsAndTransport.totalInvoice)}
                                        />
                                    </div>
                                </div>

                                <hr style={{ margin: '12px 0', borderColor: 'var(--orders-border-color)' }} />

                                {/* SHIPPER (CARRIER) COMBOBOX */}
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '12px' }}>
                                    <div>
                                        <ComboBox
                                            label="Transportadora"
                                            options={carrierOptions}
                                            value={totalsAndTransport.carrierId}
                                            onChange={handleCarrierSelect}
                                            placeholder="Selecione ou busque uma transportadora..."
                                            disabled={isLoading}
                                            loading={carriersLoading}
                                            noResultsMessage="Nenhuma transportadora encontrada"
                                        />
                                        {totalsAndTransport.carrierDocument && (
                                            <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                                                CNPJ: {formatDocument(totalsAndTransport.carrierDocument)}
                                            </small>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Placa / UF</label>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            <input
                                                type="text"
                                                className={styles.input}
                                                style={{ flex: 1 }}
                                                placeholder="Placa"
                                                value={totalsAndTransport.vehiclePlate}
                                                onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, vehiclePlate: e.target.value })}
                                                disabled={isLoading}
                                            />
                                            <select
                                                className={styles.select}
                                                style={{ width: '70px' }}
                                                value={totalsAndTransport.vehicleUf}
                                                onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, vehicleUf: e.target.value })}
                                                disabled={isLoading}
                                            >
                                                <option value="">UF</option>
                                                <option value="SP">SP</option>
                                                <option value="RJ">RJ</option>
                                                <option value="MG">MG</option>
                                                <option value="ES">ES</option>
                                                <option value="PR">PR</option>
                                                <option value="SC">SC</option>
                                                <option value="RS">RS</option>
                                                <option value="BA">BA</option>
                                                <option value="PE">PE</option>
                                                <option value="CE">CE</option>
                                                <option value="RN">RN</option>
                                                <option value="PB">PB</option>
                                                <option value="SE">SE</option>
                                                <option value="AL">AL</option>
                                                <option value="MA">MA</option>
                                                <option value="PI">PI</option>
                                                <option value="PA">PA</option>
                                                <option value="AM">AM</option>
                                                <option value="AC">AC</option>
                                                <option value="RO">RO</option>
                                                <option value="RR">RR</option>
                                                <option value="AP">AP</option>
                                                <option value="TO">TO</option>
                                                <option value="GO">GO</option>
                                                <option value="MT">MT</option>
                                                <option value="MS">MS</option>
                                                <option value="DF">DF</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '20px', marginBottom: '12px', flexWrap: 'wrap' }}>
                                    <label style={{ fontWeight: '600', fontSize: '0.9rem' }}>Frete por Conta:</label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="freightPayer"
                                            value="emitente"
                                            checked={totalsAndTransport.freightPayer === 'emitente'}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, freightPayer: e.target.value })}
                                            disabled={isLoading}
                                        />
                                        Emitente
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="freightPayer"
                                            value="destinatario"
                                            checked={totalsAndTransport.freightPayer === 'destinatario'}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, freightPayer: e.target.value })}
                                            disabled={isLoading}
                                        />
                                        Destinatário
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                        <input
                                            type="radio"
                                            name="freightPayer"
                                            value="terceiros"
                                            checked={totalsAndTransport.freightPayer === 'terceiros'}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, freightPayer: e.target.value })}
                                            disabled={isLoading}
                                        />
                                        Terceiros
                                    </label>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '12px' }}>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.85rem' }}>Quantidade</label>
                                        <input
                                            type="text"
                                            className={styles.input}
                                            value={totalsAndTransport.quantity}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, quantity: e.target.value })}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.85rem' }}>Espécie</label>
                                        <input
                                            type="text"
                                            className={styles.input}
                                            value={totalsAndTransport.species}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, species: e.target.value })}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.85rem' }}>Marca</label>
                                        <input
                                            type="text"
                                            className={styles.input}
                                            value={totalsAndTransport.brand}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, brand: e.target.value })}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.85rem' }}>Número</label>
                                        <input
                                            type="text"
                                            className={styles.input}
                                            value={totalsAndTransport.number}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, number: e.target.value })}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.85rem' }}>Peso Bruto</label>
                                        <input
                                            type="number"
                                            className={styles.input}
                                            step="0.001"
                                            value={totalsAndTransport.grossWeight}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, grossWeight: e.target.value })}
                                            disabled={isLoading}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontWeight: '500', fontSize: '0.85rem' }}>Peso Líquido</label>
                                        <input
                                            type="number"
                                            className={styles.input}
                                            step="0.001"
                                            value={totalsAndTransport.netWeight}
                                            onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, netWeight: e.target.value })}
                                            disabled={isLoading}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Mensagens Legais</label>
                                    <textarea
                                        className={styles.textarea}
                                        rows={2}
                                        value={totalsAndTransport.legalMessages}
                                        onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, legalMessages: e.target.value })}
                                        disabled={isLoading}
                                        placeholder="Mensagens legais da NF-e..."
                                    />
                                </div>
                                <div style={{ marginTop: '10px' }}>
                                    <label style={{ fontWeight: '500', fontSize: '0.9rem' }}>Informações Complementares</label>
                                    <textarea
                                        className={styles.textarea}
                                        rows={2}
                                        value={totalsAndTransport.complementaryInfo}
                                        onChange={(e) => setTotalsAndTransport({ ...totalsAndTransport, complementaryInfo: e.target.value })}
                                        disabled={isLoading}
                                        placeholder="Informações complementares..."
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formActions}>
                        <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={isLoading}>
                            <FiX size={18} /> Cancelar
                        </button>
                        <button type="button" onClick={handleCalculateTotals} className={styles.secondaryButton} disabled={isLoading}>
                            <FiRefreshCw size={18} className={isLoading ? styles.buttonSpinner : ''} /> Calcular
                        </button>
                        <button type="button" className={styles.secondaryButton} disabled={isLoading}>
                            <FiPrinter size={18} /> Espelho NF-e
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className={styles.confirmButton}
                            disabled={isLoading || !header.operationNature || !header.clientName || products.length === 0}
                        >
                            {isLoading ? (
                                <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Processando...</>
                            ) : (
                                <><FiCheck size={18} /> Confirmar Lançamento</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

// ============================================
// HELPER FUNCTIONS FOR STOCK MANAGEMENT
// ============================================

/**
 * Deduct stock from current-stock collection for a product
 * Uses FIFO (oldest stock first) based on expiration date or creation date
 */
const deductProductStock = async (product, invoiceCode, issuedBy) => {
    try {
        const isWater = isWaterMaterial(product.itemCode);

        // Water is infinite, no deduction needed
        if (isWater) {
            console.log(`Water product ${product.itemCode} - no stock deduction needed`);
            return { success: true, isWater: true, deducted: 0 };
        }

        const quantity = parseFloat(product.quantity) || 0;
        if (quantity <= 0) {
            return { success: true, deducted: 0 };
        }

        // Build query for current-stock
        const lotNumber = product.lot && product.lot.trim() !== '' ? product.lot : null;
        const stockConstraints = [
            where("rawMaterialId", "==", product.productId),
            where("quantity", ">", 0.001)
        ];

        // Add lot filter if specified
        if (lotNumber) {
            stockConstraints.push(where("lotNumber", "==", lotNumber));
        }

        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            ...stockConstraints
        );

        const stockSnapshot = await getDocs(stockQuery);

        if (stockSnapshot.empty) {
            console.warn(`No stock found for product: ${product.productId}, lot: ${lotNumber || 'N/A'}`);
            return { success: false, error: 'No stock available', available: 0 };
        }

        // Calculate total available stock
        let totalAvailable = 0;
        const stockDocs = [];
        stockSnapshot.forEach(doc => {
            const data = doc.data();
            const qty = data.quantity || 0;
            totalAvailable += qty;
            stockDocs.push({
                id: doc.id,
                ref: doc.ref,
                data: data,
                quantity: qty
            });
        });

        // Check if enough stock available
        if (totalAvailable < quantity) {
            return {
                success: false,
                error: 'Insufficient stock',
                available: totalAvailable,
                required: quantity
            };
        }

        // Sort by expiration date (oldest first - FIFO)
        stockDocs.sort((a, b) => {
            const dateA = a.data.expirationDate || a.data.createdAt || 0;
            const dateB = b.data.expirationDate || b.data.createdAt || 0;
            return dateA > dateB ? 1 : -1;
        });

        // Deduct stock using batch
        const batch = writeBatch(db);
        let remainingToDeduct = quantity;
        let deducted = 0;

        for (const doc of stockDocs) {
            if (remainingToDeduct <= 0.001) break;

            const currentQty = doc.quantity;
            const deductAmount = Math.min(currentQty, remainingToDeduct);
            const newQty = currentQty - deductAmount;

            if (newQty > 0.001) {
                batch.update(doc.ref, {
                    quantity: newQty,
                    updatedAt: serverTimestamp()
                });
            } else {
                batch.delete(doc.ref);
            }

            remainingToDeduct -= deductAmount;
            deducted += deductAmount;
        }

        await batch.commit();

        // Record outflow in stock-outflows collection
        await addDoc(collection(db, STOCK_OUTFLOWS_COLLECTION), {
            rawMaterialId: product.productId,
            rawMaterialCode: product.itemCode,
            rawMaterialName: product.description,
            quantity: quantity,
            lotNumber: lotNumber || '',
            expirationDate: product.expirationDate || null,
            locationId: 'INVOICE_SALE',
            locationDescription: `Venda - NF ${invoiceCode}`,
            type: 'SAIDA',
            notes: `Venda manual - NF ${invoiceCode}`,
            createdBy: issuedBy,
            createdAt: serverTimestamp(),
            isProduct: true
        });

        return {
            success: true,
            deducted: deducted,
            totalAvailable: totalAvailable
        };

    } catch (error) {
        console.error('Error deducting stock:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const InvoicingIssuingPage = () => {
    const { currentUser } = useAuth();
    const can = usePagePermissions('faturamento_emissao_de_nf');
    const { addNotification } = useNotifications();

    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [toasts, setToasts] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [issuingOrder, setIssuingOrder] = useState(null);
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);

    const { data: orders, loading: ordersLoading, refreshData } = useFirestore(
        SALES_ORDERS_COLLECTION,
        'createdAt',
        true
    );

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(toast => toast.id !== id)), 5000);
    };

    const billingOrders = useMemo(() => {
        if (!orders) return [];
        return orders.filter(order => order.status === 'ready_for_billing');
    }, [orders]);

    const filteredOrders = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return billingOrders.filter(order =>
            (order.code && order.code.toLowerCase().includes(lowerSearch)) ||
            (order.clientName && order.clientName.toLowerCase().includes(lowerSearch))
        ).sort((a, b) => new Date(b.readyForBillingAt || 0) - new Date(a.readyForBillingAt || 0));
    }, [billingOrders, searchTerm]);

    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
    const currentOrders = filteredOrders.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    const handleConfirmInvoice = async (orderId, invoiceData) => {
        setActionLoading(true);
        try {
            const order = orders.find(o => o.id === orderId);
            const existingHistory = order?.trackingHistory || [];

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: 'Faturado',
                description: `Nota Fiscal emitida (NF-e: ${invoiceData.nfeNumber}). Câmbio aplicado: R$ ${invoiceData.exchangeRate.toFixed(4)} / USD.`,
                updatedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
            };

            const orderRef = doc(db, SALES_ORDERS_COLLECTION, orderId);
            await updateDoc(orderRef, {
                status: 'invoiced',
                invoiceData: {
                    nfeNumber: invoiceData.nfeNumber,
                    series: invoiceData.series,
                    accessKey: invoiceData.accessKey,
                    cfop: invoiceData.cfop,
                    issueDate: invoiceData.issueDate,
                    notes: invoiceData.notes,
                    exchangeRate: invoiceData.exchangeRate,
                    usdSnapshot: invoiceData.usdSnapshot,
                    brlValues: invoiceData.brlValues,
                    issuedAt: new Date().toISOString(),
                    issuedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
                },
                // Replace order items with converted versions (preserving both currencies)
                items: invoiceData.itemsConverted,
                // Store BRL values at the top level so the order list shows BRL after invoicing
                calculatedValues: invoiceData.brlValues,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: serverTimestamp()
            });

            addToast(
                `Pedido ${order.code} faturado com sucesso! Total: ${formatCurrency(invoiceData.brlValues.total)}`,
                'Success'
            );
            await refreshData();
            setIssuingOrder(null);
        } catch (error) {
            console.error('Error issuing invoice:', error);
            addToast(`Erro ao lançar nota fiscal: ${error.message}`, 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmManualInvoice = async (manualData) => {
        setActionLoading(true);
        try {
            // Create invoice document
            const invoiceData = {
                isManualNfe: true,
                status: 'invoiced',
                code: `NFE-MAN-${Date.now().toString().slice(-6)}`,
                clientName: manualData.header.clientName || 'Cliente Manual',
                clientId: manualData.header.clientId,
                clientDocument: manualData.header.clientDocument,
                operationNature: manualData.header.operationNature,
                entryExitDate: manualData.header.entryExitDate,
                products: manualData.products.map(p => ({
                    ...p,
                    isWater: isWaterMaterial(p.itemCode)
                })),
                totals: manualData.totalsAndTransport,
                billingConditionId: manualData.header.billingCondition,
                accountExecutiveId: manualData.header.accountExecutive,
                carrier: {
                    id: manualData.totalsAndTransport.carrierId,
                    name: manualData.totalsAndTransport.carrierName,
                    document: manualData.totalsAndTransport.carrierDocument,
                    plate: manualData.totalsAndTransport.vehiclePlate,
                    uf: manualData.totalsAndTransport.vehicleUf,
                    freightPayer: manualData.totalsAndTransport.freightPayer,
                },
                issuedBy: manualData.issuedBy,
                issuedAt: manualData.createdAt,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                clientData: manualData.clientData,
                carrierData: manualData.carrierData,
            };

            // Save invoice
            const docRef = await addDoc(collection(db, SALES_ORDERS_COLLECTION), invoiceData);
            const invoiceCode = invoiceData.code;

            // Process stock deduction for each product
            const stockResults = [];
            for (const product of manualData.products) {
                if (product.productId && product.quantity > 0) {
                    const result = await deductProductStock(
                        product,
                        invoiceCode,
                        manualData.issuedBy
                    );
                    stockResults.push({
                        product: product.itemCode,
                        ...result
                    });

                    if (!result.success && !result.isWater) {
                        console.warn(`Stock issue for ${product.itemCode}:`, result.error);
                        addToast(`Atenção: Estoque insuficiente para ${product.itemCode}. Disponível: ${result.available || 0}, Solicitado: ${result.required || product.quantity}`, 'Warning');
                    }
                }
            }

            const criticalFailures = stockResults.filter(r => !r.success && !r.isWater);
            if (criticalFailures.length > 0) {
                addToast(`Nota fiscal criada com alertas de estoque para ${criticalFailures.length} produto(s).`, 'Warning');
            } else {
                addToast(`Nota Fiscal manual lançada com sucesso! (${invoiceCode})`, 'Success');
            }

            await refreshData();
            setIsManualModalOpen(false);
        } catch (error) {
            console.error('Error issuing manual NFe:', error);
            addToast(`Erro ao lançar nota fiscal manual: ${error.message}`, 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className={styles.pageContainer}>
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {issuingOrder && (
                <ExchangeRateModal
                    order={issuingOrder}
                    onClose={() => setIssuingOrder(null)}
                    onConfirm={handleConfirmInvoice}
                    isLoading={actionLoading}
                />
            )}

            {isManualModalOpen && (
                <ManualInvoiceModal
                    onClose={() => setIsManualModalOpen(false)}
                    onConfirm={handleConfirmManualInvoice}
                    isLoading={actionLoading}
                />
            )}

            <PageHeader
                title="Faturamento (Emissão de NF)"
                actionText={can.create ? "Lançamento de NF Manual" : undefined}
                onAction={can.create ? () => setIsManualModalOpen(true) : undefined}
                showAction={can.create}
            />

            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por código ou cliente..."
                    onSearch={(value) => { setSearchTerm(value); setCurrentPage(1); }}
                    initialValue={searchTerm}
                    disabled={actionLoading}
                />
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>NF / Código</th>
                            <th>Cliente</th>
                            <th>Data Empacotamento</th>
                            <th className="text-right">Caixas</th>
                            <th className="text-right">Valor Total (USD)</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {ordersLoading && billingOrders.length === 0 ? (
                            <tr><td colSpan="7"><TableSkeleton columns={7} rows={ITEMS_PER_PAGE} /></td></tr>
                        ) : currentOrders.length > 0 ? (
                            currentOrders.map(order => {
                                // If already invoiced, show BRL; otherwise show USD
                                const isInvoiced = order.status === 'invoiced';
                                const displayTotal = isInvoiced
                                    ? (order.invoiceData?.brlValues?.total ?? order.calculatedValues?.total ?? 0)
                                    : (order.usdValues?.total ?? order.calculatedValues?.total ?? 0);

                                return (
                                    <tr key={order.id} className={styles.tableRow}>
                                        <td><strong>{order.code}</strong></td>
                                        <td>{order.clientName}</td>
                                        <td>{formatDate(order.readyForBillingAt || order.updatedAt)}</td>
                                        <td className="text-right">{order.totalBoxes || '-'}</td>
                                        <td className="text-right" style={{ fontWeight: 'bold' }}>
                                            {isInvoiced ? formatCurrency(displayTotal) : formatCurrencyUSD(displayTotal)}
                                        </td>
                                        <td>
                                            <StatusBadge status={order.status} />
                                        </td>
                                        <td>
                                            <div className={styles.actionsContainer}>
                                                {can.approve && (
                                                    <button
                                                        onClick={() => setIssuingOrder(order)}
                                                        className={`${styles.actionButton} ${styles.invoiceButton}`}
                                                        disabled={actionLoading}
                                                        title="Lançar Nota Fiscal"
                                                    >
                                                        <FiFileText size={14} /> Faturar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7">
                                    <EmptyState
                                        icon="🧾"
                                        title="Nenhum pedido aguardando faturamento"
                                        message="Pedidos finalizados na etapa de embalagem aparecerão aqui."
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {!ordersLoading && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOrders.length}
                    onPageChange={setCurrentPage}
                    disabled={actionLoading}
                />
            )}
        </div>
    );
};

export default InvoicingIssuingPage;